/**
 * D48: the question-form test — lifted out of `parse-command.ts` at the
 * file-size cap (D212), unchanged in what it decides.
 *
 * @packageDocumentation
 */

import { type CompiledLexicon } from '../lexicon/compile-lexicon.js';
import { type ClauseStructure } from './match-clause-structure.js';

/**
 * Did this clause's head intent arrive through a QUESTION-FORM alias (D48)?
 *
 * @remarks
 * The standard vocabulary declares `what is`, `where is` and `how is` as
 * multiword aliases of `inspect` — question-form aliases, and a good feature:
 * `what is the bar` parses straight to an inspect without the shape ladder
 * being needed. The cost is that a question whose subject resolves to nothing
 * fails as a missing ENTITY, never reaching the ladder's question rung.
 *
 * The test is structural, not a word list: the intent was matched as an alias,
 * and the alias's first word is one the VOCABULARY declares an interrogative
 * leader. A pack that declares neither gets `false` and behaves exactly as
 * before.
 *
 * @param structure - The matched clause structure
 * @param lexicon - The compiled vocabulary (its interrogative leaders)
 * @returns Whether the head intent came from a question-form alias
 */
export const isQuestionFormIntent = (structure: ClauseStructure, lexicon: Pick<CompiledLexicon, 'interrogativeLeaderSet'>): boolean => {
	if (!('intentToken' in structure) || !structure.intentToken.wasAliasMatched) {
		return false;
	}

	const [leadingAliasWord] = structure.intentToken.sourceText.split(' ');

	return leadingAliasWord !== undefined && lexicon.interrogativeLeaderSet.has(leadingAliasWord);
};
